import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from '../ui/button';
import { Plus, Trash2, Clock, ShoppingCart } from 'lucide-react';
import { reservationViewModel } from '../../view-model/professional/reservation-view-model'
import { BookingDetail } from '../../model/professional/booking-model';
import ClientInfoForm from './client-info-form';
import OptionCategory from './option-category';
import CompactDatePicker from '../ui/compact-date-picker';
import CollaboratorSelect from './collaborator-select';
import PromoCodeInput from '../client/promo-code-input';
import { t } from 'i18next';

interface AppointmentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  showTitle?: boolean;
  editMode?: boolean;
  bookingToEdit?: BookingDetail | null;
}

const ReservationDashboard: React.FC<AppointmentFormProps> = ({
  onSuccess,
  onCancel,
  showTitle = true,
  editMode = false,
  bookingToEdit = null
}) => {
  const viewModel = reservationViewModel;
  const [showServiceSelect, setShowServiceSelect] = useState(true);

  // Charger les services au montage du composant
  useEffect(() => {
    viewModel.loadServices().then();

    // Si mode édition, pré-remplir le formulaire
    if (editMode && bookingToEdit) {
      viewModel.loadBookingForEdit(bookingToEdit);
    }

    return () => {
      viewModel.resetForm();
    };
  }, [editMode, bookingToEdit, viewModel]);

  // Recalculer l'heure de fin quand la durée totale ou l'heure de début change
  useEffect(() => {
    if (viewModel.manualStartTime && viewModel.totalDuration > 0) {
      const [hours, minutes] = viewModel.manualStartTime.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);
      const endDate = new Date(startDate.getTime() + viewModel.totalDuration * 60000);
      const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
      viewModel.setManualEndTime(endTime);
    }
  }, [viewModel.totalDuration, viewModel.selectedServices.length, viewModel.manualStartTime]);

  const handleCollaboratorChange = (collaboratorId: string) => {
    viewModel.setSelectedCollaboratorCompagnieId(collaboratorId);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    viewModel.setSelectedDate(e.target.value);
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    viewModel.setManualStartTime(e.target.value);
    // Calculer automatiquement l'heure de fin
    if (e.target.value && viewModel.totalDuration > 0) {
      const [hours, minutes] = e.target.value.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);
      const endDate = new Date(startDate.getTime() + viewModel.totalDuration * 60000);
      const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
      viewModel.setManualEndTime(endTime);
    }
  };

  const handleOptionCheckboxChange = (categoryId: string, optionId: string, checked: boolean) => {
    viewModel.setOptionCheckboxValue(categoryId, optionId, checked);
  };

  const handleOptionSelectChange = (categoryId: string, e: React.ChangeEvent<HTMLSelectElement>) => {
    viewModel.setOptionSelectValue(categoryId, e.target.value);
  };

  const handleFirstNameChange = (value: string) => {
    viewModel.setClientFirstName(value);
  };

  const handleLastNameChange = (value: string) => {
    viewModel.setClientLastName(value);
  };

  const handleEmailChange = (value: string) => {
    viewModel.setClientEmail(value);
  };

  const handlePhoneChange = (value: string) => {
    viewModel.setClientPhone(value);
  };

  const handleAddService = () => {
    viewModel.addServiceToBooking();
    setShowServiceSelect(false);
  };

  const handleRemoveService = (index: number) => {
    viewModel.removeServiceFromBooking(index);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await viewModel.submitForm();
    if (success) {
      onSuccess();
    }
  };

  const formatPrice = (price: number | string | undefined | null) => {
    const numPrice = typeof price === 'number' ? price : parseFloat(String(price || 0));
    return numPrice.toFixed(2).replace('.', ',');
  };

  const getItemTotalPrice = (item: any) => {
    const servicePrice = typeof item.service.price === 'number'
      ? item.service.price
      : parseFloat(String(item.service.price || 0));
    let total = servicePrice;

    if (item.service.optionCategories) {
      for (const category of item.service.optionCategories) {
        if (category.options) {
          for (const option of category.options) {
            if (item.optionIds.includes(option.id)) {
              const optionPrice = typeof option.price === 'number'
                ? option.price
                : parseFloat(String(option.price || 0));
              total += optionPrice;
            }
          }
        }
      }
    }

    return total;
  };

  const getItemTotalDuration = (item: any) => {
    let total = item.service.duration || 0;

    if (item.service.optionCategories) {
      for (const category of item.service.optionCategories) {
        if (category.options) {
          for (const option of category.options) {
            if (item.optionIds.includes(option.id) && option.durationMinutes) {
              total += option.durationMinutes;
            }
          }
        }
      }
    }

    return total;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1 pb-28 md:pb-12">
      {showTitle && (
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('dashboard.newManualAppointment', 'Nouveau rendez-vous manuel')}
        </h3>
      )}

      <div className="text-xs text-center text-gray-500 dark:text-gray-400 mb-2">
        {t('dashboard.requiredFields', 'Les champs marqués d\'un')} <span className="text-red-500">*</span> {t('dashboard.requiredFields2', 'sont obligatoires.')}
      </div>

      {/* 1. SÉLECTION DU SERVICE */}
      <div className="space-y-4 p-4 bg-primary/5 dark:bg-primary/10 rounded-lg border-2 border-primary/20 dark:border-primary/30">
        <div className="flex items-center gap-2">
          <ShoppingCart size={18} className="text-primary dark:text-primary-400" />
          <h4 className="text-sm font-medium text-primary dark:text-primary-400">
            {showServiceSelect
              ? t('dashboard.selectService', 'Sélectionner un service')
              : t('dashboard.services', 'Services')}
          </h4>
        </div>

        {!showServiceSelect ? (
          <Button
            type="button"
            onClick={() => setShowServiceSelect(true)}
            className="w-full"
            variant="outline"
          >
            <Plus size={16} className="mr-2" />
            {t('dashboard.addService', 'Ajouter un service')}
          </Button>
        ) : (
          <div className="space-y-2">
            {viewModel.isLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                {t('dashboard.loadingServices', 'Chargement des services...')}
              </p>
            ) : viewModel.services.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                {t('dashboard.noServices', 'Aucun service disponible')}
              </p>
            ) : (
              viewModel.services
                .filter((service: any) => service.isActive !== false)
                .map((service: any) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={async () => {
                      const initialServicesCount = viewModel.selectedServices.length;
                      await viewModel.selectAndAddService(service.id);
                      if (viewModel.selectedServices.length > initialServicesCount) {
                        setShowServiceSelect(false);
                      }
                    }}
                    className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {service.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {service.duration} min • {formatPrice(service.price)}€
                      </p>
                    </div>
                    <Plus size={16} className="text-gray-400" />
                  </button>
                ))
            )}

            {/* 2. OPTIONS (si le service en a) */}
            {viewModel.selectedService && viewModel.optionCategories.length > 0 && (
              <div className="space-y-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('dashboard.options', 'Options pour')} {viewModel.serviceDetails?.name}
                </h5>

                {viewModel.optionCategories.map(category => (
                  <OptionCategory
                    key={category.id}
                    category={category}
                    selectedOptions={viewModel.selectedOptions[category.id] || []}
                    onOptionChange={(optionId, checked) =>
                      handleOptionCheckboxChange(category.id, optionId, checked)
                    }
                    onSelectChange={(e) => handleOptionSelectChange(category.id, e)}
                  />
                ))}

                <Button
                  type="button"
                  onClick={handleAddService}
                  className="w-full"
                >
                  <Plus size={16} className="mr-2" />
                  {t('dashboard.addToCart', 'Ajouter au panier')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PANIER - Liste des services sélectionnés */}
      {viewModel.selectedServices.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <ShoppingCart size={16} />
            {t('dashboard.cart', 'Panier')} ({viewModel.selectedServices.length})
          </h4>
          {viewModel.selectedServices.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {item.service.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getItemTotalDuration(item)} min • {formatPrice(getItemTotalPrice(item))}€
                  {item.optionIds.length > 0 && ` • ${item.optionIds.length} option(s)`}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveService(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* 3. SÉLECTION DU COLLABORATEUR (seulement si plusieurs) */}
      {viewModel.selectedServices.length > 0 && (
        <CollaboratorSelect
          value={viewModel.selectedCollaboratorCompagnieId}
          onChange={handleCollaboratorChange}
          disabled={viewModel.isLoading}
        />
      )}

      {/* 4. DATE ET HORAIRES */}
      {viewModel.selectedServices.length > 0 && viewModel.selectedCollaboratorCompagnieId && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('dashboard.dateAndTime', 'Date et horaires')}
          </h4>

          <CompactDatePicker
            name="appointmentDate"
            value={viewModel.selectedDate}
            onChange={handleDateChange}
            disabled={viewModel.isLoading}
            label="Date"
            required
            min={new Date().toISOString().split('T')[0]}
          />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('dashboard.startTime', 'Heure de début')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="time"
                  id="startTime"
                  value={viewModel.manualStartTime}
                  onChange={handleStartTimeChange}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('dashboard.endTime', 'Heure de fin (calculée)')}
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="time"
                  id="endTime"
                  value={viewModel.manualEndTime}
                  disabled
                  className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 dark:text-gray-100 cursor-not-allowed opacity-60"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('dashboard.calculatedFromDuration', 'Calculée automatiquement selon la durée totale')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 5. INFORMATIONS CLIENT */}
      {viewModel.selectedServices.length > 0 && viewModel.selectedCollaboratorCompagnieId && viewModel.selectedDate && viewModel.manualStartTime && viewModel.manualEndTime && (
        <ClientInfoForm
          firstName={viewModel.clientFirstName}
          lastName={viewModel.clientLastName}
          email={viewModel.clientEmail}
          phone={viewModel.clientPhone}
          onFirstNameChange={handleFirstNameChange}
          onLastNameChange={handleLastNameChange}
          onEmailChange={handleEmailChange}
          onPhoneChange={handlePhoneChange}
        />
      )}

      {/* CODE PROMO */}
      {viewModel.selectedServices.length > 0 && viewModel.selectedCollaboratorCompagnieId && viewModel.selectedDate && viewModel.manualStartTime && viewModel.manualEndTime && (
        <PromoCodeInput
          promoCode={viewModel.promoCode}
          isValidating={viewModel.isValidatingPromoCode}
          isValid={viewModel.promoCodeValidated}
          discountAmount={viewModel.promoCodeDiscount}
          validationMessage={viewModel.promoCodeMessage}
          onPromoCodeChange={(code) => viewModel.setPromoCode(code)}
          onValidate={() => viewModel.validatePromoCode()}
          onRemove={() => viewModel.resetPromoCode()}
          enableDarkMode={true}
        />
      )}

      {/* TOTAL */}
      {viewModel.selectedServices.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-primary/5 dark:bg-primary/10 rounded-lg border border-primary/20 dark:border-primary/30">
            <div className="flex-1">
              <p className="text-sm font-medium text-primary dark:text-primary-400">
                Total
              </p>
              <p className="text-xs text-primary dark:text-primary-400">
                {viewModel.totalDuration} min
              </p>
            </div>
            <div className="text-right">
              {viewModel.promoCodeValidated && viewModel.promoCodeDiscount > 0 ? (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-through">
                    {formatPrice(viewModel.totalPrice)}€
                  </p>
                  <p className="text-sm font-bold text-primary dark:text-primary-400">
                    {formatPrice(viewModel.finalPrice)}€
                  </p>
                </>
              ) : (
                <p className="text-sm font-bold text-primary dark:text-primary-400">
                  {formatPrice(viewModel.totalPrice)}€
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="rounded-lg py-3 sm:py-2.5 px-4 w-full sm:w-auto"
          disabled={viewModel.isLoading}
        >
          {t('dashboard.cancel', 'Annuler')}
        </Button>
        <Button
          type="submit"
          className="rounded-lg py-3 sm:py-2.5 px-4 w-full sm:w-auto"
          disabled={!viewModel.isFormValid() || viewModel.isLoading}
        >
          {viewModel.isLoading ? t('dashboard.loading', 'Chargement...') : t('dashboard.confirm', 'Confirmer le rendez-vous')}
        </Button>
      </div>
    </form>
  );
};

export default observer(ReservationDashboard);
